import type {DeepPartial} from '@/global';
import type {AiPromptBox} from '@Components/ai-prompt-box/ai-prompt-box.types';
import type {InspirationData} from '@AiChat/components/ai-chat-window-inspirations';
import {scrollAndUpdatePrompt} from '@AiChat/components/ai-chat-window-inspirations/ai-chat-window-inspirations.data';
import type {
  AiChatEmptyHomeState,
  AiChatSeasonConfig,
  AiChatSeasonDates,
  AiChatSeasonInspiration,
  AiChatSeasonPageState,
  AiChatSeasonSuggestion,
} from '@AiChat/lib/ai-chat-season-library.types';
import {AI_CHAT_SEASON_CONFIGS, AI_CHAT_SEASON_SUGGESTIONS_PER_SET, AiChatSeason} from '@AiChat/lib/ai-chat-season-library.types';

const MONTH_DAY_KEY_MULTIPLIER = 100;

let todaysAiChatSeason: AiChatSeason | null | undefined;

export const getAiChatSeasonForPage = (state: AiChatSeasonPageState): AiChatSeason | null => {
  if (state.isEmbedded || !isAiChatEmptyHome(state)) {
    return null;
  }

  return getTodaysAiChatSeason();
};

export const isAiChatEmptyHome = (state: AiChatEmptyHomeState): boolean => {
  return !state.currentChatId && !state.isQuickTipsView && !state.isAssetsView && !state.isMemoryView;
};

export const getActiveAiChatSeason = (date: Date): AiChatSeason | null => {
  const activeSeason = Object.values(AiChatSeason).find((season) => {
    return isDateInAiChatSeason(date, AI_CHAT_SEASON_CONFIGS[season].dates);
  });

  return activeSeason ?? null;
};

export const getAiChatSeasonConfig = (season: AiChatSeason): AiChatSeasonConfig => {
  return AI_CHAT_SEASON_CONFIGS[season];
};

export const getAiChatSeasonSuggestionSet = (season: AiChatSeason, setIndex: number): AiChatSeasonSuggestion[] => {
  const firstSuggestionIndex = setIndex * AI_CHAT_SEASON_SUGGESTIONS_PER_SET;

  return getAiChatSeasonConfig(season).suggestions.slice(firstSuggestionIndex, firstSuggestionIndex + AI_CHAT_SEASON_SUGGESTIONS_PER_SET);
};

export const getNextAiChatSeasonSuggestionSetIndex = (season: AiChatSeason, setIndex: number): number => {
  const setCount = Math.ceil(getAiChatSeasonConfig(season).suggestions.length / AI_CHAT_SEASON_SUGGESTIONS_PER_SET);

  return (setIndex + 1) % setCount;
};

export const getAiChatSeasonInspirations = (season: AiChatSeason, updatePromptBox: (options: DeepPartial<AiPromptBox>) => void): InspirationData[] => {
  return getAiChatSeasonConfig(season).inspirations.map((inspiration) => {
    return getAiChatSeasonInspiration(inspiration, updatePromptBox);
  });
};

const getTodaysAiChatSeason = (): AiChatSeason | null => {
  if (todaysAiChatSeason === undefined) {
    todaysAiChatSeason = getActiveAiChatSeason(new Date());
  }

  return todaysAiChatSeason;
};

const isDateInAiChatSeason = (date: Date, dates: AiChatSeasonDates): boolean => {
  const dateKey = getMonthDayKey(date.getMonth(), date.getDate());
  const startKey = getMonthDayKey(dates.startMonthIndex, dates.startDay);
  const endKey = getMonthDayKey(dates.endMonthIndex, dates.endDay);

  return dateKey >= startKey && dateKey <= endKey;
};

const getMonthDayKey = (monthIndex: number, day: number): number => {
  return monthIndex * MONTH_DAY_KEY_MULTIPLIER + day;
};

const getAiChatSeasonInspiration = (inspiration: AiChatSeasonInspiration, updatePromptBox: (options: DeepPartial<AiPromptBox>) => void): InspirationData => {
  const prompt = window.i18next.t(inspiration.promptKey);

  return {
    visualLayoutVariant: inspiration.visualLayoutVariant,
    imageURL: inspiration.card,
    pillText: window.i18next.t(inspiration.pillTextKey),
    pillIcon: inspiration.pillIcon,
    prompt,
    widthVariant: inspiration.widthVariant,
    onRecreate: (): void => {
      scrollAndUpdatePrompt(prompt, [], updatePromptBox);
    },
  };
};
